import React from 'react';
import { ArrowRight, Sparkles, Users, BarChart3, Brain, Award, RefreshCw } from 'lucide-react';
import { trackCTAClick } from '../utils/analytics';

const differentiators = [
  {
    icon: Brain,
    title: 'Science-Backed Methodology',
    traditional: 'Generic one-size-fits-all content',
    ours: 'DISC behavioral science, adaptive communication, and evidence-based leadership frameworks tailored to your team',
    color: 'cyan',
  },
  {
    icon: Users,
    title: 'Hands-On, Not Lecture-Based',
    traditional: 'Passive PowerPoint presentations',
    ours: 'Interactive workshops with real-time exercises, team activities, and applied learning that sticks',
    color: 'violet',
  },
  {
    icon: BarChart3,
    title: 'Measurable Outcomes',
    traditional: 'Vague "feel good" sessions with no tracking',
    ours: 'Documented results: engagement scores, retention metrics, communication improvements, and leadership benchmarks',
    color: 'emerald',
  },
  {
    icon: Sparkles,
    title: 'AI-Enhanced Learning',
    traditional: 'Outdated training materials',
    ours: 'Modern AI-integrated tools that accelerate learning, personalize development paths, and future-proof your workforce',
    color: 'amber',
  },
  {
    icon: RefreshCw,
    title: 'Follow-Through & Accountability',
    traditional: 'One-day events with no follow-up',
    ours: 'Multi-session programs with coaching, check-ins, and reinforcement that ensures lasting behavioral change',
    color: 'rose',
  },
  {
    icon: Award,
    title: 'Cross-Industry Expertise',
    traditional: 'Narrow industry focus',
    ours: 'Proven results across corporate, government, education, nonprofit, and community organizations',
    color: 'blue',
  },
];

const colorMap = {
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400', icon: 'text-cyan-400' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-400', icon: 'text-violet-400' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: 'text-emerald-400' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', icon: 'text-amber-400' },
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/20', text: 'text-rose-400', icon: 'text-rose-400' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', icon: 'text-blue-400' },
};

const WhyChooseUs = () => {
  const scrollToConsultation = () => {
    trackCTAClick('See the difference firsthand', 'why_choose_us');
    document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="why-choose-us" className="py-16 sm:py-24 bg-slate-950" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-violet-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            The Difference
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Organizations Choose Us
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
              Because…
            </span>
          </h2>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            We're not another training company. We deliver transformation that's measurable, lasting, and built for real organizations.
          </p>
        </div>

        {/* Differentiators Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {differentiators.map((d, i) => {
            const c = colorMap[d.color];
            return (
              <div
                key={i}
                className="p-5 sm:p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all duration-300 group"
                data-testid={`differentiator-${i}`}
              >
                <div className={`w-10 h-10 rounded-xl ${c.bg} ${c.border} border flex items-center justify-center mb-4`}>
                  <d.icon className={`h-5 w-5 ${c.icon}`} />
                </div>
                <h3 className="text-white font-bold text-base mb-4">{d.title}</h3>

                {/* Traditional */}
                <div className="mb-3 p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                  <p className="text-red-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Traditional Training</p>
                  <p className="text-slate-500 text-sm leading-relaxed line-through decoration-red-500/30">{d.traditional}</p>
                </div>

                {/* Ours */}
                <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                  <p className="text-emerald-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Culture Align</p>
                  <p className="text-slate-200 text-sm leading-relaxed">{d.ours}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Micro CTA */}
        <div className="text-center mt-10 sm:mt-12">
          <button
            onClick={scrollToConsultation}
            data-testid="why-choose-us-cta"
            className="inline-flex items-center gap-2 text-white font-semibold hover:text-violet-400 transition-colors text-sm group/cta"
          >
            See the difference firsthand
            <ArrowRight className="h-4 w-4 group-hover/cta:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
